import {Component, inject} from '@angular/core';
import {Router} from '@angular/router';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {NzColDirective, NzRowDirective} from 'ng-zorro-antd/grid';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {TranslatePipe} from '@ngx-translate/core';
import {APP_THEMES, AppTheme, AppThemeSpec, ThemeService} from '@app/service/theme.service';

/**
 * 「主题」对话框：八个主题各一张卡，点一下立即生效并关窗。
 *
 * 写法照 {@link LanguageChangeComponent} 那一套（`inject(NzModalRef)`、`ok()` 关窗、
 * `nz-row` + `nz-col` + `nz-card [nzHoverable]` + 当前那张打勾）。
 *
 * **八张卡就照 service 那张表铺**（{@link APP_THEMES}）：主题清单只有那一份，
 * 这里不另写一遍 —— 卡片上的名字、副标题、两个色块全取自表里的字段；
 * 连**摆放顺序**也跟着那张表走（4 × 2、同列同色），所以这个组件里没有一条主题清单。
 */
@Component({
  selector: 'app-theme-change',
  templateUrl: './theme.change.component.html',
  styleUrl: './theme.change.component.less',
  imports: [
    NzRowDirective,
    NzColDirective,
    NzCardModule,
    NzButtonModule,
    NzIconModule,
    TranslatePipe,
  ],
})
export class ThemeChangeComponent {

  readonly theme = inject(ThemeService);
  readonly #modal = inject(NzModalRef);
  private readonly router = inject(Router);

  /** 全部主题（九个：八个预置 + 一个自定义）。 */
  protected readonly themes = APP_THEMES;

  /** 卡片上主色那块：自定义主题取用户当前配置，预置取表里的静态值。 */
  protected primaryOf(spec: AppThemeSpec): string {
    return spec.custom ? this.theme.customPreview().primary : spec.primary;
  }

  /** 卡片上底色那块：自定义主题是亮底（同蔚蓝），预置取表里的静态值。 */
  protected groundOf(spec: AppThemeSpec): string {
    return spec.custom ? this.theme.customPreview().ground : spec.ground;
  }

  /** 底部「自定义主题」入口：先关窗再跳到自定义页。 */
  protected goCustom(): void {
    this.#modal.destroy(undefined);
    this.router.navigate(['/main/theme']).then(() => {});
  }

  cancel(): void {
    this.#modal.destroy(undefined);
  }

  ok(): void {
    this.#modal.destroy(undefined);
  }

  /**
   * 选中一个主题。**先应用再关窗**：`setTheme` 是同步生效的（换 `<link>` 的 href），
   * 关窗之后不会再有「等它变」的空档。点的就是当前那个时 `setTheme` 自己会早退，
   * 于是这一下等价于单纯关窗。
   */
  protected changeTheme(id: AppTheme): void {
    this.theme.setTheme(id);
    this.ok();
  }
}
